/**
 * Macro Calculator Hook
 * Manages macro plan creation and weight tracking
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type {
  ClientWeightLog,
  MacroPlanInput,
  WeightLogInput,
  NutritionGoalWithPlan,
  NutritionDailySummary,
} from '@/lib/nutrition/types';
import {
  analyzeBaseline,
  analyzeProgress,
  type NutritionLogSummary,
  type WeightEntry,
} from '@/lib/nutrition/macro-insights';

// =============================================================================
// Weight Logs Hook
// =============================================================================

export function useClientWeightLogs(clientId: string | undefined) {
  return useQuery({
    queryKey: ['client-weight-logs', clientId],
    queryFn: async () => {
      if (!clientId) return [];

      const { data, error } = await supabase
        .from('client_weight_logs')
        .select('*')
        .eq('client_id', clientId)
        .order('logged_date', { ascending: false })
        .limit(90); // Last 90 days

      if (error) throw error;
      return (data || []) as ClientWeightLog[];
    },
    enabled: !!clientId,
  });
}

export function useAddWeightLog() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: WeightLogInput) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('client_weight_logs')
        .upsert(
          {
            client_id: input.client_id,
            weight_lbs: input.weight_lbs,
            logged_date: input.logged_date || new Date().toISOString().split('T')[0],
            notes: input.notes || null,
            logged_by: session.session.user.id,
          },
          {
            onConflict: 'client_id,logged_date',
          }
        )
        .select()
        .single();

      if (error) throw error;
      return data as ClientWeightLog;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-weight-logs', variables.client_id] });
      toast({
        title: 'Weight logged',
        description: `${variables.weight_lbs} lbs recorded.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// =============================================================================
// Macro Plan Hook
// =============================================================================

export function useCreateMacroPlan() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: MacroPlanInput) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user) throw new Error('Not authenticated');

      // End any existing active goal for this client
      await supabase
        .from('nutrition_goals')
        .update({ effective_until: new Date().toISOString().split('T')[0] })
        .eq('client_id', input.client_id)
        .is('effective_until', null);

      // Create new goal with macro plan settings
      const { data, error } = await supabase
        .from('nutrition_goals')
        .insert({
          client_id: input.client_id,
          user_id: session.session.user.id,
          goal_type: input.goal_type,
          baseline_source: input.baseline_source,
          baseline_tdee: input.baseline_tdee,
          calorie_adjustment: input.calorie_adjustment,
          protein_per_lb: input.protein_per_lb,
          fat_per_lb: input.fat_per_lb,
          activity_level: input.activity_level || null,
          starting_weight_lbs: input.starting_weight_lbs,
          plan_started_at: new Date().toISOString().split('T')[0],
          target_calories: input.target_calories,
          target_protein_g: input.target_protein_g,
          target_carbs_g: input.target_carbs_g,
          target_fat_g: input.target_fat_g,
          target_fiber_g: input.target_fiber_g,
          target_water_oz: input.target_water_oz,
          effective_from: new Date().toISOString().split('T')[0],
          notes: input.notes || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate all nutrition-related caches with refetchType 'all' to force refetch
      queryClient.invalidateQueries({
        queryKey: ['nutrition-goals', variables.client_id],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['client-active-goal', variables.client_id],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['nutrition-summary', variables.client_id],
        refetchType: 'all'
      });
      toast({
        title: 'Macro plan created',
        description: 'New nutrition targets have been set.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useClientActiveGoal(clientId: string | undefined) {
  return useQuery({
    queryKey: ['client-active-goal', clientId],
    queryFn: async () => {
      if (!clientId) return null;

      const today = new Date().toISOString().split('T')[0];

      // Order by effective_from DESC, then created_at DESC to get newest goal
      const { data, error } = await supabase
        .from('nutrition_goals')
        .select('*')
        .eq('client_id', clientId)
        .lte('effective_from', today)
        .or(`effective_until.is.null,effective_until.gte.${today}`)
        .order('effective_from', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
      return (data as NutritionGoalWithPlan) || null;
    },
    enabled: !!clientId,
  });
}

// =============================================================================
// Baseline Analysis Hook
// =============================================================================

export function useBaselineAnalysis(clientId: string | undefined, days: number = 14) {
  const { data: weightLogs } = useClientWeightLogs(clientId);

  return useQuery({
    queryKey: ['baseline-analysis', clientId, days],
    queryFn: async () => {
      if (!clientId) return null;

      // Fetch nutrition summaries for the period
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data: summaries, error } = await supabase
        .from('nutrition_daily_summaries')
        .select('*')
        .eq('client_id', clientId)
        .gte('summary_date', startDate.toISOString().split('T')[0])
        .order('summary_date', { ascending: true });

      if (error) throw error;

      // Convert to the format expected by analyzeBaseline
      const nutritionLogs: NutritionLogSummary[] = (summaries || []).map((s: NutritionDailySummary) => ({
        date: s.summary_date,
        total_calories: s.total_calories,
        total_protein_g: s.total_protein_g,
        total_carbs_g: s.total_carbs_g,
        total_fat_g: s.total_fat_g,
      }));

      const weightEntries: WeightEntry[] = (weightLogs || []).map((w: ClientWeightLog) => ({
        date: w.logged_date,
        weight_lbs: w.weight_lbs,
      }));

      return analyzeBaseline(nutritionLogs, weightEntries);
    },
    enabled: !!clientId && !!weightLogs,
  });
}

// =============================================================================
// Progress Analysis Hook
// =============================================================================

export function useProgressAnalysis(clientId: string | undefined) {
  const { data: activeGoal } = useClientActiveGoal(clientId);
  const { data: weightLogs } = useClientWeightLogs(clientId);

  return useQuery({
    queryKey: ['progress-analysis', clientId, activeGoal?.id],
    queryFn: async () => {
      if (!clientId || !activeGoal || !activeGoal.plan_started_at) return null;

      // Fetch nutrition summaries since plan started
      const { data: summaries, error } = await supabase
        .from('nutrition_daily_summaries')
        .select('*')
        .eq('client_id', clientId)
        .gte('summary_date', activeGoal.plan_started_at)
        .order('summary_date', { ascending: true });

      if (error) throw error;

      // Convert to expected format
      const nutritionLogs: NutritionLogSummary[] = (summaries || []).map((s: NutritionDailySummary) => ({
        date: s.summary_date,
        total_calories: s.total_calories,
        total_protein_g: s.total_protein_g,
        total_carbs_g: s.total_carbs_g,
        total_fat_g: s.total_fat_g,
      }));

      const weightEntries: WeightEntry[] = (weightLogs || [])
        .filter((w: ClientWeightLog) => w.logged_date >= activeGoal.plan_started_at!)
        .map((w: ClientWeightLog) => ({
          date: w.logged_date,
          weight_lbs: w.weight_lbs,
        }));

      return analyzeProgress(
        nutritionLogs,
        weightEntries,
        activeGoal.target_calories || 0,
        activeGoal.calorie_adjustment || 0,
        activeGoal.plan_started_at
      );
    },
    enabled: !!clientId && !!activeGoal && !!weightLogs,
  });
}

// =============================================================================
// Client Profile for Calculator
// =============================================================================

interface ClientWithMeasurements {
  id: string;
  name: string;
  email: string | null;
  date_of_birth: string | null;
  sex: 'male' | 'female' | null;
}

// Unit conversions — the calculator works in imperial, the DB stores metric.
const CM_PER_INCH = 2.54;
const LBS_PER_KG = 2.20462;

/**
 * Persists the physical profile the trainer typed into the calculator back
 * onto the client, so it prefills next time instead of being retyped for every
 * plan. Height/age/sex were previously entered and then discarded.
 *
 * Age is converted to an approximate date of birth (Jan 1 of the birth year):
 * the calculator collects age, but storing a date means the value doesn't go
 * stale as the client gets older. Precise DOB comes from client onboarding.
 */
export function useSaveClientProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      client_id: string;
      height_inches?: number | null;
      age?: number | null;
      sex?: 'male' | 'female' | null;
    }) => {
      const updates: Record<string, unknown> = {};

      if (input.height_inches != null && Number.isFinite(input.height_inches)) {
        updates.height_cm = Math.round(input.height_inches * CM_PER_INCH);
      }
      if (input.sex) {
        updates.biological_sex = input.sex;
      }
      if (input.age != null && Number.isFinite(input.age) && input.age > 0) {
        const birthYear = new Date().getFullYear() - Math.round(input.age);
        updates.date_of_birth = `${birthYear}-01-01`;
      }

      if (Object.keys(updates).length === 0) return null;

      const { error } = await supabase
        .from('clients')
        .update(updates)
        .eq('id', input.client_id);

      if (error) throw error;
      return updates;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['client-for-calculator', variables.client_id],
      });
    },
    // Deliberately silent: this is a convenience write alongside plan creation.
    // If it fails the plan itself still saved, and surfacing a second toast
    // would imply the plan failed too.
    onError: (error) => {
      console.error('Failed to persist client profile from calculator:', error);
    },
  });
}

/** Whole years between a date of birth and today. */
export function ageFromDob(dob: string | null): number | null {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age >= 0 && age < 130 ? age : null;
}


/**
 * Loads the client profile the macro calculator prefills from.
 *
 * Previously selected `clients.date_of_birth` (which did not exist — Postgres
 * returned 42703 and the thrown error left `data` permanently undefined) and
 * read height from a `measurements` table that does not exist either. Both
 * date_of_birth and biological_sex were added to `clients` in migration
 * add_client_dob_and_biological_sex_for_macros; height lives on
 * `clients.height_cm`.
 *
 * The calculator works in imperial, so convert on the way out.
 */
export function useClientForCalculator(clientId: string | undefined) {
  const { data: weightLogs } = useClientWeightLogs(clientId);

  return useQuery({
    queryKey: ['client-for-calculator', clientId],
    queryFn: async () => {
      if (!clientId) return null;

      const { data: client, error } = await supabase
        .from('clients')
        .select('id, name, email, date_of_birth, biological_sex, height_cm, weight_kg')
        .eq('id', clientId)
        .single();

      if (error) throw error;

      // Prefer a logged weight (it's current); fall back to the onboarding value.
      const latestWeight =
        weightLogs?.[0]?.weight_lbs ??
        (client.weight_kg != null
          ? Math.round(Number(client.weight_kg) * LBS_PER_KG * 10) / 10
          : null);

      return {
        id: client.id,
        name: client.name,
        email: client.email,
        age: ageFromDob(client.date_of_birth),
        height_inches:
          client.height_cm != null
            ? Math.round((client.height_cm / CM_PER_INCH) * 10) / 10
            : null,
        latest_weight_lbs: latestWeight,
        sex: (client.biological_sex as 'male' | 'female' | null) ?? null,
      };
    },
    // Weight logs are optional — don't block the whole profile on them, or a
    // client with no logged weight never gets height/age/sex prefilled either.
    enabled: !!clientId,
  });
}
